/**
 * ShiftControlWidget — Compact shift controls for the driver Home page
 * Visible only for internal drivers (fleet_status === 'interne').
 * Shows: Start Shift, Pause/Resume, active shift timer.
 * Links to full Shift page for end-of-shift/signature.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import axios from 'axios';
import {
  PlayCircle, PauseCircle, Coffee, Clock, Loader2, Car, ChevronRight
} from 'lucide-react';
import config from 'config';

const API = config.API_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token') || localStorage.getItem('token')}` });

const fmtDuration = (mins) => {
  if (!mins || mins <= 0) return '0h00';
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${h}h${String(m).padStart(2, '0')}`;
};

const fmtTime = (iso) => {
  if (!iso) return '--:--';
  try { return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); }
  catch { return '--:--'; }
};

export default function ShiftControlWidget() {
  const navigate = useNavigate();
  const [shift, setShift] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showStartForm, setShowStartForm] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [kmStart, setKmStart] = useState('');
  const [elapsed, setElapsed] = useState(0);

  const isPaused = shift?.pauses?.length > 0 && !shift.pauses[shift.pauses.length - 1]?.ended_at;

  const fetchCurrent = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/chauffeur/shifts/current`, { headers: getHeaders() });
      setShift(data.shift);
    } catch { /* no active shift */ }
    setLoading(false);
  }, []);

  const fetchVehicles = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/chauffeur/shifts/vehicles`, { headers: getHeaders() });
      setVehicles(data.vehicles || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchCurrent(); fetchVehicles(); }, [fetchCurrent, fetchVehicles]);

  // Live elapsed timer
  useEffect(() => {
    if (!shift || shift.status !== 'IN_PROGRESS') return;
    const calcElapsed = () => {
      const start = new Date(shift.started_at).getTime();
      const now = Date.now();
      const totalPauseMins = (shift.pauses || []).reduce((sum, p) => {
        const pStart = new Date(p.started_at).getTime();
        const pEnd = p.ended_at ? new Date(p.ended_at).getTime() : now;
        return sum + (pEnd - pStart) / 60000;
      }, 0);
      return Math.max(0, (now - start) / 60000 - totalPauseMins);
    };
    setElapsed(calcElapsed());
    const interval = setInterval(() => setElapsed(calcElapsed()), 30000);
    return () => clearInterval(interval);
  }, [shift]);

  const handleStartShift = async () => {
    if (!selectedVehicle || !kmStart) return;
    setActionLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/chauffeur/shifts/start`, {
        vehicle_id: selectedVehicle,
        km_start: parseFloat(kmStart),
      }, { headers: getHeaders() });
      setShift(data.shift);
      setShowStartForm(false);
      setKmStart('');
      setSelectedVehicle('');
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors du demarrage');
    }
    setActionLoading(false);
  };

  const handleTogglePause = async () => {
    setActionLoading(true);
    try {
      if (isPaused) {
        await axios.post(`${API}/api/chauffeur/shifts/current/pause/end`, {}, { headers: getHeaders() });
      } else {
        await axios.post(`${API}/api/chauffeur/shifts/current/pause/start`, {}, { headers: getHeaders() });
      }
      await fetchCurrent();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    }
    setActionLoading(false);
  };

  if (loading) return null; // Don't show anything while loading

  // ─── Active Shift Card ───
  if (shift && shift.status === 'IN_PROGRESS') {
    const currentVehicle = shift.vehicles?.[shift.vehicles.length - 1];
    return (
      <Card className={`border ${isPaused ? 'bg-amber-500/5 border-amber-500/20' : 'bg-emerald-500/5 border-emerald-500/20'} mb-4`} data-testid="shift-active-widget">
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${isPaused ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400 animate-pulse'}`} />
              <span className={`text-xs font-semibold ${isPaused ? 'text-amber-400' : 'text-emerald-400'}`}>
                {isPaused ? 'Shift en pause' : 'Shift en cours'}
              </span>
            </div>
            <span className="text-white/40 text-[10px]">{fmtTime(shift.started_at)}</span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`text-lg font-bold ${isPaused ? 'text-amber-400' : 'text-emerald-400'}`} data-testid="shift-elapsed-time">
                {fmtDuration(elapsed)}
              </div>
              {currentVehicle && (
                <span className="text-white/30 text-[10px] flex items-center gap-1">
                  <Car className="w-3 h-3" /> {currentVehicle.plate || currentVehicle.vehicle_id}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={handleTogglePause}
                disabled={actionLoading}
                className={`h-9 px-3 text-xs font-semibold ${
                  isPaused
                    ? 'bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 border border-emerald-500/20'
                    : 'bg-amber-500/15 text-amber-400 hover:bg-amber-500/25 border border-amber-500/20'
                }`}
                data-testid="shift-pause-btn"
              >
                {actionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                  isPaused ? <><PlayCircle className="w-3.5 h-3.5 mr-1" />Reprendre</> : <><PauseCircle className="w-3.5 h-3.5 mr-1" />Pause</>}
              </Button>
              <button
                onClick={() => navigate('/chauffeur/shift')}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/40"
                data-testid="shift-details-link"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ─── No Active Shift: Show Start Button or Form ───
  if (showStartForm) {
    return (
      <Card className="bg-[#161616] border-[#C9A24D]/20 mb-4" data-testid="shift-start-form">
        <CardContent className="p-3 space-y-2.5">
          <div className="flex items-center justify-between">
            <span className="text-white font-semibold text-xs">Demarrer un shift</span>
            <button onClick={() => setShowStartForm(false)} className="text-white/30 hover:text-white text-xs">Annuler</button>
          </div>
          <select value={selectedVehicle} onChange={e => setSelectedVehicle(e.target.value)}
            className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2 text-white text-xs focus:border-[#C9A24D]/50 focus:outline-none"
            data-testid="shift-vehicle-select">
            <option value="">Choisir un vehicule</option>
            {vehicles.map(v => (
              <option key={v.id} value={v.id} disabled={v.in_use}>
                {v.brand} {v.model} - {v.license_plate}{v.in_use ? ' (utilisé)' : ''}
              </option>
            ))}
          </select>
          <input type="number" value={kmStart} onChange={e => setKmStart(e.target.value)}
            placeholder="Kilometrage depart"
            className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2 text-white text-xs focus:border-[#C9A24D]/50 focus:outline-none"
            data-testid="shift-km-start" />
          <Button
            onClick={handleStartShift}
            disabled={actionLoading || !selectedVehicle || !kmStart}
            className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-black font-semibold text-xs h-9"
            data-testid="shift-start-confirm-btn"
          >
            {actionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><PlayCircle className="w-3.5 h-3.5 mr-1.5" />Demarrer le shift</>}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // ─── Start Shift Button ───
  return (
    <button
      onClick={() => setShowStartForm(true)}
      className="w-full flex items-center gap-3 mb-4 px-4 py-3 rounded-xl bg-[#C9A24D]/8 border border-[#C9A24D]/15 hover:bg-[#C9A24D]/12 transition-colors"
      data-testid="shift-start-widget"
    >
      <PlayCircle className="w-5 h-5 text-[#C9A24D]" />
      <div className="flex-1 text-left">
        <p className="text-[#C9A24D] text-xs font-semibold">Demarrer un shift</p>
        <p className="text-white/30 text-[10px]">Pointage et suivi du temps de travail</p>
      </div>
      <ChevronRight className="w-4 h-4 text-[#C9A24D]/50" />
    </button>
  );
}
